import { useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { ChangelogApiClient, Release } from './api/ChangelogApiClient';
import { Calendar, FileText, AlertCircle } from 'lucide-react';
import './markdown-styles.css';
import { PrismLogo } from '../../assets/icons';

const MarkdownRender = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const apiClient = useMemo(
    () => new ChangelogApiClient({ discoveryApi }),
    [discoveryApi],
  );

  const [releases, setReleases] = useState<Release[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [contentLoading, setContentLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch releases list on mount
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedReleases = await apiClient.getReleases();
        setReleases(fetchedReleases);

        // Auto-select the first (latest) release
        if (fetchedReleases.length > 0) {
          setSelectedRelease(fetchedReleases[0]);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load releases';
        setError(errorMessage);
        // eslint-disable-next-line no-console
        console.error('Failed to fetch releases:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReleases();
  }, [apiClient]);

  // Fetch markdown content when selected release changes
  useEffect(() => {
    const fetchMarkdownContent = async () => {
      if (!selectedRelease) {
        setMarkdown('');
        return;
      }

      try {
        setContentLoading(true);
        setError(null);

        // Fetch the content from the pre-signed URL
        const content = await apiClient.getMarkdownContent(selectedRelease.url);
        setMarkdown(content);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load markdown content';
        setError(errorMessage);
        // eslint-disable-next-line no-console
        console.error('Failed to fetch markdown content:', err);
      } finally {
        setContentLoading(false);
      }
    };

    fetchMarkdownContent();
  }, [selectedRelease, apiClient]);

  const handleReleaseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const releaseId = event.target.value;
    const release = releases.find(r => r.id === releaseId);
    if (release) {
      setSelectedRelease(release);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              <div className="text-center">
                <p className="text-lg font-medium text-gray-700">
                  Changelog is loading, please wait...
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Fetching the latest changelog files
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && releases.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center gap-4 p-6 bg-red-50 rounded-lg border-l-4 border-red-500">
              <AlertCircle size={24} className="text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-900">
                  Error Loading Changelogs
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (releases.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center py-12">
              <FileText
                size={64}
                className="mx-auto opacity-30 mb-4 text-gray-400"
              />
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                No Releases Found
              </h2>
              <p className="text-base text-gray-600">
                There are no release changelogs available at this time.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg text-white">
                <PrismLogo />
              </div>
              <div className="flex flex-col gap-1">
                <h1 className="text-3xl font-bold text-gray-900 m-0">
                  PRISM API Changelog
                </h1>
              </div>
            </div>

            <div className="min-w-[280px]">
              <label
                htmlFor="changelog-select"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select Release
              </label>
              <select
                id="changelog-select"
                value={selectedRelease?.id || ''}
                onChange={handleReleaseChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {releases.map(release => (
                  <option key={release.id} value={release.id}>
                    {release.displayDate}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {selectedRelease && (
            <div className="flex items-center gap-4 p-4 mb-6 bg-blue-50 rounded-lg border-l-4 border-blue-600">
              <Calendar size={20} className="text-blue-600" />
              <p className="text-base text-gray-900">
                <strong>Release Date:</strong> {selectedRelease.displayDate}
              </p>
            </div>
          )}

          {contentLoading && (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              <div className="text-center">
                <p className="text-lg font-medium text-gray-700">
                  Content is loading, please wait...
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Fetching the changelog content
                </p>
              </div>
            </div>
          )}

          {!contentLoading && error && (
            <div className="flex items-center gap-4 p-6 bg-red-50 rounded-lg border-l-4 border-red-500">
              <AlertCircle size={24} className="text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-900">
                  Error Loading Content
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {!contentLoading && !error && (
            <div className="markdown-content">
              <ReactMarkdown>{markdown}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarkdownRender;
